<template>
    <PageHeader :title="$t('profile_edit.title')" @back="goBack" />

    <div class="flex-1 space-y-6 overflow-y-auto p-4">
        <div class="flex flex-col items-center space-y-4 pt-4">
            <div class="relative">
                <Avatar :size="40" :src="formData.avatar" :alt="formData.tag_name" />
                <button
                    type="button"
                    @click="$refs.avatarInput.click()"
                    class="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-red-500 p-0.5"
                >
                    <svg
                        class="size-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M21 3V21H3V3H21ZM5 15.4805V19H19V17.4141L16 14.4141L14.707 15.707L13.375 15.7812L9 12.2812L5 15.4805ZM5 12.918L8.375 10.2188H9.625L13.9258 13.6592L15.293 12.293H16.707L19 14.5859V5H5V12.918ZM16.5 7V10.5H13V7H16.5Z"
                            fill="white"
                        />
                    </svg>
                </button>
            </div>
            <input
                ref="avatarInput"
                type="file"
                accept="image/*"
                @change="handleAvatarChange"
                class="hidden"
            />
            <p class="text-sm text-zinc-400">{{ $t('profile_edit.tap_to_change') }}</p>
        </div>

        <Panel size="full" padding="p-5">
            <div class="space-y-4">
                <div class="flex items-center justify-between">
                    <h3 class="font-title text-sm font-bold uppercase">{{ $t('profile_edit.edit_name') }}</h3>
                    <svg
                        class="size-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M20 16V20L18 21V17H6V21L4 20V16L5 15H19L20 16ZM15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12C13.6569 12 15 10.6569 15 9ZM17 9C17 11.7614 14.7614 14 12 14C9.23858 14 7 11.7614 7 9C7 6.23858 9.23858 4 12 4C14.7614 4 17 6.23858 17 9Z"
                            fill="#FF2C2C"
                        />
                    </svg>
                </div>

                <div>
                    <label for="current_name" class="mb-2 block text-sm text-white">
                        {{ $t('profile_edit.current_name') }}
                    </label>
                    <input
                        id="current_name"
                        :value="originalData.tag_name"
                        type="text"
                        disabled
                        class="border-stroke-1 w-full border bg-zinc-900 px-4 py-3 text-zinc-500"
                    />
                </div>

                <TagNameInput
                    v-model="formData.tag_name"
                    :label="$t('profile_edit.new_name')"
                    :placeholder="$t('profile_edit.new_name_placeholder')"
                    :current-name="originalData.tag_name"
                    :show-counter="false"
                    @update:status="tagNameStatus = $event"
                />

                <DiagonalCutButton
                    @click="handleSaveTagName"
                    :disabled="
                        isSavingTagName ||
                        !hasTagNameChanges ||
                        tagNameStatus === 'taken' ||
                        tagNameStatus === 'checking'
                    "
                    :loading="isSavingTagName"
                    variant="secondary"
                    cut-position="left-top"
                    class="w-full"
                >
                    {{ isSavingTagName ? $t('profile_edit.saving') : $t('profile_edit.confirm_name') }}
                </DiagonalCutButton>
            </div>
        </Panel>

        <Panel size="full" padding="p-5">
            <div class="space-y-4">
                <div class="flex items-center justify-between">
                    <h3 class="font-title text-sm font-bold uppercase">{{ $t('profile_edit.edit_nationality') }}</h3>
                    <svg
                        class="size-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"
                            fill="#FF2C2C"
                        />
                    </svg>
                </div>

                <div>
                    <label for="current_nationality" class="mb-2 block text-sm text-white">
                        {{ $t('profile_edit.current_nationality') }}
                    </label>
                    <input
                        id="current_nationality"
                        :value="currentNationalityName"
                        type="text"
                        disabled
                        class="border-stroke-1 w-full border bg-zinc-900 px-4 py-3 text-zinc-500"
                    />
                </div>

                <div>
                    <label for="new_nationality" class="mb-2 block text-sm text-white">
                        {{ $t('profile_edit.new_nationality') }}
                    </label>
                    <div class="relative">
                        <select
                            id="new_nationality"
                            v-model="formData.nationality"
                            required
                            data-testid="nationality-select"
                            class="border-stroke-1 bg-card w-full appearance-none border px-4 py-3 pr-10 text-white transition-colors focus:border-red-500 focus:outline-none"
                        >
                            <option
                                v-for="country in countries"
                                :key="country.code"
                                :value="country.code"
                            >
                                {{ country.name }}
                            </option>
                        </select>
                        <div
                            class="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-gray-400"
                        >
                            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                    </div>
                </div>

                <DiagonalCutButton
                    @click="handleSaveNationality"
                    :disabled="isSavingNationality || !hasNationalityChanges"
                    :loading="isSavingNationality"
                    variant="secondary"
                    cut-position="left-top"
                    class="w-full"
                    data-testid="save-nationality-button"
                >
                    {{ isSavingNationality ? $t('profile_edit.saving') : $t('profile_edit.confirm_nationality') }}
                </DiagonalCutButton>
            </div>
        </Panel>

        <!-- <Panel v-if="translationsStore.canSwitchLocale" size="full" padding="p-5">
            <div class="flex items-center justify-between">
                <h3 class="font-title text-sm font-bold uppercase">{{ $t('profile.language') }}</h3>
                <SelectDrawer v-model="selectedLanguage" :presets="languageOptions" class="w-40">
                    {{ $t('profile.language') }}
                </SelectDrawer>
            </div>
        </Panel> -->

        <Panel size="full" padding="p-5">
            <AnnouncerToggle />
        </Panel>

        <Panel size="full" padding="p-5">
            <div class="flex items-center justify-between">
                <div>
                    <h3 class="font-title mb-2 text-sm font-bold uppercase">{{ $t('profile_edit.refresh_data') }}</h3>
                    <p class="text-sm text-zinc-400">{{ $t('profile_edit.refresh_data_hint') }}</p>
                </div>
                <button type="button" @click="refreshData" class="text-red-600 hover:text-red-500">
                    <svg
                        class="size-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M20.7002 13.5369C20.2995 15.507 19.3111 17.2978 17.8701 18.6395C16.4281 19.982 14.6091 20.8029 12.6797 20.9686C10.7499 21.1343 8.82611 20.6346 7.19434 19.5516C6.35293 18.9931 5.61436 18.2945 5 17.492V19.8377H3V13.6102H8.91895V15.6102H6.13184C6.68846 16.5283 7.4281 17.3059 8.2998 17.8846C9.56226 18.7225 11.0378 19.1027 12.5088 18.9764C13.98 18.85 15.3831 18.2228 16.5078 17.1756C17.6335 16.1275 18.4199 14.713 18.7402 13.1375L20.7002 13.5369ZM11.3213 3.03303C13.2506 2.86736 15.1741 3.36761 16.8057 4.45002C17.6306 4.99733 18.3576 5.67827 18.9648 6.46076V3.99982H20.9648V4.16291H21V10.3904H20.9648V10.4012H14.8848V8.40119H15.085V8.39045H17.8682C17.3113 7.4725 16.572 6.69446 15.7002 6.11603C14.438 5.27863 12.9627 4.89991 11.4922 5.02619C10.0214 5.15254 8.6187 5.77933 7.49414 6.82599C6.36879 7.87351 5.58161 9.28653 5.26074 10.8612L3.30078 10.4627C3.70201 8.49325 4.69103 6.7032 6.13184 5.36213C7.57371 4.02013 9.39234 3.19867 11.3213 3.03303Z"
                            fill="#FF2C2C"
                        />
                    </svg>
                </button>
            </div>
        </Panel>
    </div>

    <Modal
        :is-visible="showDeleteConfirmModal"
        :on-confirm="confirmDeleteMatches"
        :on-decline="cancelDeleteMatches"
        :is-loading="isSavingRefresh"
    >
        <h2 class="font-title mb-2 text-base leading-normal font-extrabold text-white uppercase">
            {{ $t('profile_edit.delete_matches_title') }}
        </h2>
        <p class="mt-4 text-sm leading-normal font-normal text-white">
            {{ $t('profile_edit.delete_matches_body') }}
        </p>
    </Modal>

    <Modal
        :is-visible="showUnsavedChangesModal"
        :on-confirm="confirmLeaveWithoutSaving"
        :on-decline="cancelLeave"
    >
        <h2 class="font-title mb-2 text-base leading-normal font-extrabold text-white uppercase">
            {{ $t('profile_edit.unsaved_title') }}
        </h2>
        <p class="mt-4 text-sm leading-normal font-normal text-white">
            {{ $t('profile_edit.unsaved_body') }}
        </p>
    </Modal>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useTranslationsStore } from '@/stores/translations.js'
import { apiService } from '@/services/api.js'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import PageHeader from '@/components/PageHeader.vue'
import Panel from '@/components/layout/Panel.vue'
import Avatar from '@/components/Avatar.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import TagNameInput from '@/components/ui/TagNameInput.vue'
import AnnouncerToggle from '@/components/AnnouncerToggle.vue'
import Modal from '@/components/Modal.vue'
import { countries } from '@/constants/countries.js'
import { useImageCompression } from '@/composables/useImageCompression.js'
// import SelectDrawer from '@/components/ui/SelectDrawer.vue'

export default {
    name: 'ProfileEditView',
    components: {
        PageHeader,
        Panel,
        Avatar,
        DiagonalCutButton,
        TagNameInput,
        AnnouncerToggle,
        Modal,
        // SelectDrawer,
    },
    data() {
        return {
            formData: {
                tag_name: '',
                avatar: '',
                nationality: '',
            },
            originalData: {},
            tagNameStatus: 'idle',
            isSavingTagName: false,
            isSavingNationality: false,
            isSavingRefresh: false,
            countries,
            showDeleteConfirmModal: false,
            showUnsavedChangesModal: false,
            languageOptions: [
                { value: 'en', label: 'English' },
                { value: 'nl', label: 'Nederlands' },
                { value: 'de', label: 'Deutsch' },
                { value: 'fr', label: 'Français' },
                { value: 'it', label: 'Italiano' },
                { value: 'pl', label: 'Polski' },
                { value: 'es', label: 'Español' },
                { value: 'da', label: 'Dansk' },
                { value: 'cs', label: 'Čeština' },

            ],
        }
    },
    computed: {
        ...mapStores(useAuthStore, useTranslationsStore),

        selectedLanguage: {
            get() {
                return this.translationsStore.locale
            },
            set(value) {
                if (value && value !== this.translationsStore.locale) {
                    this.translationsStore.setLocale(value)
                }
            },
        },

        hasTagNameChanges() {
            return (
                this.formData.tag_name !== this.originalData.tag_name &&
                this.formData.tag_name.trim() !== ''
            )
        },

        hasNationalityChanges() {
            return this.formData.nationality !== this.originalData.nationality
        },

        hasChanges() {
            return this.hasTagNameChanges || this.hasNationalityChanges
        },

        currentNationalityName() {
            const country = this.countries.find(
                (c) => c.code === this.originalData.nationality?.trim(),
            )
            return country?.name || this.$t('profile_edit.not_set')
        },
    },
    mounted() {
        this.loadProfileData()
    },
    methods: {
        loadProfileData() {
            const profile = this.authStore.profile
            if (profile) {
                this.originalData = {
                    tag_name: profile.tag_name || '',
                    avatar: profile.avatar || '',
                    nationality: profile.nationality?.trim() || '',
                }
                this.formData = {
                    tag_name: '',
                    avatar: profile.avatar || '',
                    nationality: profile.nationality?.trim() || '',
                }
            }
        },

        async handleAvatarChange(event) {
            const file = event.target.files[0]
            if (!file) return

            const { processImage, createPreview } = useImageCompression()

            const result = await processImage(file, {
                maxSizeMB: 5,
                targetSizeMB: 4.5,
                maxWidthOrHeight: 1920,
            })

            if (!result.success) {
                this.$toast(this.$t('general.error_title'), 3000, result.error)
                event.target.value = ''
                return
            }

            this.isSavingTagName = true

            try {
                this.formData.avatar = await createPreview(result.file)

                const formData = new FormData()
                formData.append('_method', 'PUT')
                formData.append('avatar', result.file)

                const response = await fetch(`${apiService.baseURL}/api/v1/user/avatar`, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${this.authStore.token}`,
                    },
                    body: formData,
                })

                const data = await response.json()

                if (!response.ok) {
                    const error = new Error(`Update avatar failed: ${response.status}`)
                    error.response = { data, status: response.status }
                    throw error
                }

                await this.authStore.verifyToken()
                this.originalData.avatar = this.formData.avatar

                this.$analytics.trackEvent(ANALYTICS_EVENTS.AVATAR_UPLOADED)

                this.$toast(this.$t('general.success_title'), 3000, this.$t('profile_edit.success_avatar'))
            } catch (error) {
                console.error('Failed to update avatar:', error)

                this.formData.avatar = this.originalData.avatar

                let errorMessage = this.$t('general.error_retry')
                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors
                    const firstError = Object.values(errors)[0]
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                }
                this.$toast(this.$t('general.error_title'), 3000, errorMessage)
            } finally {
                this.isSavingTagName = false
                event.target.value = ''
            }
        },

        refreshData() {
            this.showDeleteConfirmModal = true
        },

        async confirmDeleteMatches() {
            this.isSavingRefresh = true
            this.showDeleteConfirmModal = false

            try {
                await apiService.delete('/api/v1/matches/delete-all')

                // Refresh user data to update match lists
                await this.authStore.verifyToken()

                this.$toast(this.$t('general.success_title'), 3000, this.$t('profile_edit.success_matches_deleted'))
            } catch (error) {
                console.error('Failed to delete matches:', error)

                let errorMessage = this.$t('general.error_retry')
                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors
                    const firstError = Object.values(errors)[0]
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                }
                this.$toast(this.$t('general.error_title'), 3000, errorMessage)
            } finally {
                this.isSavingRefresh = false
            }
        },

        cancelDeleteMatches() {
            this.showDeleteConfirmModal = false
        },

        async handleSaveTagName() {
            if (this.isSavingTagName || !this.hasTagNameChanges) return

            if (!this.formData.tag_name.trim()) {
                this.$toast(this.$t('general.error_title'), 3000, this.$t('profile_edit.error_missing_name'))
                return
            }

            this.isSavingTagName = true

            try {
                await apiService.put('/api/v1/user/tag-name', {
                    tag_name: this.formData.tag_name.trim(),
                })

                await this.authStore.verifyToken()

                this.originalData.tag_name = this.formData.tag_name.trim()
                this.formData.tag_name = ''

                // The field name only - never the value, which is a real person's chosen name.
                this.$analytics.trackEvent(ANALYTICS_EVENTS.PROFILE_EDITED, { field: 'tag_name' })

                this.$toast(this.$t('general.success_title'), 3000, this.$t('profile_edit.success_name'))
            } catch (error) {
                console.error('Failed to update profile name:', error)

                const data = error.response?.data || error.data
                let errorMessage = this.$t('general.error_retry')
                if (data?.errors) {
                    const firstError = Object.values(data.errors)[0]
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
                } else if (data?.message) {
                    errorMessage = data.message
                }
                this.$toast(this.$t('general.error_title'), 3000, errorMessage)
            } finally {
                this.isSavingTagName = false
            }
        },

        async handleSaveNationality() {
            if (this.isSavingNationality || !this.hasNationalityChanges) return

            this.isSavingNationality = true

            try {
                await apiService.put('/api/v1/user/nationality', {
                    nationality: this.formData.nationality,
                })

                await this.authStore.verifyToken()

                this.$analytics.trackEvent(ANALYTICS_EVENTS.PROFILE_EDITED, {
                    field: 'nationality',
                })

                this.originalData.nationality = this.formData.nationality

                this.$toast(this.$t('general.success_title'), 3000, this.$t('profile_edit.success_nationality'))
            } catch (error) {
                console.error('Failed to update nationality:', error)

                let errorMessage = this.$t('general.error_retry')
                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors
                    const firstError = Object.values(errors)[0]
                    errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                }
                this.$toast(this.$t('general.error_title'), 3000, errorMessage)
            } finally {
                this.isSavingNationality = false
            }
        },

        goBack() {
            if (this.hasChanges) {
                this.showUnsavedChangesModal = true
            } else {
                this.$router.push({ name: 'profile' })
            }
        },

        confirmLeaveWithoutSaving() {
            this.showUnsavedChangesModal = false
            this.$router.go(-1)
        },

        cancelLeave() {
            this.showUnsavedChangesModal = false
        },
    },
}
</script>
